import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import {
  az,
  bg,
  bn,
  ca,
  cs,
  da,
  de,
  el,
  enUS,
  es,
  et,
  faIR,
  fi,
  fr,
  he,
  hi,
  hr,
  hu,
  hy,
  id,
  is,
  it,
  ja,
  ka,
  ko,
  lt,
  lv,
  ms,
  nb,
  nl,
  pl,
  pt,
  ptBR,
  ro,
  ru,
  sk,
  sl,
  sq,
  sr,
  sv,
  ta,
  th,
  tr,
  uk,
  vi,
  zhCN,
  zhTW,
} from 'date-fns/locale/index.js';

const MINUTE_IN_MILLISECONDS = 60 * 1000;
const HOUR_IN_MILLISECONDS = 60 * MINUTE_IN_MILLISECONDS;
const DAY_IN_MILLISECONDS = 24 * HOUR_IN_MILLISECONDS;
const MONTH_IN_MILLISECONDS = 30 * DAY_IN_MILLISECONDS;
const YEAR_IN_MILLISECONDS = 365 * DAY_IN_MILLISECONDS;

const DATE_FNS_LOCALES = {
  az,
  bg,
  bn,
  ca,
  cs,
  da,
  de,
  el,
  en: enUS,
  en_US: enUS,
  es,
  et,
  fa: faIR,
  fi,
  fr,
  he,
  hi,
  hr,
  hu,
  hy,
  id,
  is,
  it,
  ja,
  ka,
  ko,
  lt,
  lv,
  ms,
  nb,
  nl,
  no: nb,
  pl,
  pt,
  pt_BR: ptBR,
  ro,
  ru,
  sk,
  sl,
  sq,
  sr,
  sv,
  ta,
  th,
  tr,
  uk,
  vi,
  zh: zhCN,
  zh_CN: zhCN,
  zh_TW: zhTW,
};

const extractLocaleValue = locale => {
  if (!locale) return null;
  return typeof locale === 'object' && 'value' in locale
    ? locale.value
    : locale;
};

const getSelectedLocale = () => {
  if (typeof window === 'undefined') return 'en';

  const locale =
    extractLocaleValue(window.WOOT_WIDGET?.$root?.$i18n?.locale) ||
    window.chatwootConfig?.selectedLocale ||
    document?.documentElement?.lang ||
    navigator?.language ||
    'en';

  return typeof locale === 'string' && locale.trim() ? locale : 'en';
};

const getDateFnsLocale = () => {
  const normalizedLocale = getSelectedLocale().replace(/-/g, '_');

  return (
    DATE_FNS_LOCALES[normalizedLocale] ||
    DATE_FNS_LOCALES[normalizedLocale.split('_')[0]] ||
    enUS
  );
};

const isRussianLocale = () => {
  return getSelectedLocale().replace(/_/g, '-').split('-')[0] === 'ru';
};

const parseDate = time => {
  if (time instanceof Date) {
    return Number.isNaN(time.getTime()) ? null : time;
  }

  if (typeof time === 'string') {
    const trimmed = time.trim();
    if (!trimmed) return null;

    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const parsedDate = new Date(trimmed);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const numericTime = Number(trimmed);
    return numericTime > 1e12
      ? new Date(numericTime)
      : fromUnixTime(numericTime);
  }

  if (typeof time !== 'number' || Number.isNaN(time)) {
    return null;
  }

  return time > 1e12 ? new Date(time) : fromUnixTime(time);
};

const formatWithLocale = (time, datePattern) => {
  const parsedDate = parseDate(time);
  if (!parsedDate) return '';

  return format(parsedDate, datePattern, { locale: getDateFnsLocale() });
};

const formatRussianDateTime = (time, { includeTime = false } = {}) => {
  if (!isRussianLocale()) return null;

  const parsedDate = parseDate(time);
  if (!parsedDate) return '';

  const hasCurrentYear = isSameYear(parsedDate, new Date());

  if (includeTime) {
    return formatWithLocale(
      parsedDate,
      hasCurrentYear ? 'd MMMM, HH:mm' : 'd MMMM yyyy, HH:mm'
    );
  }

  return formatWithLocale(
    parsedDate,
    hasCurrentYear ? 'd MMMM' : 'd MMMM yyyy'
  );
};

const formatRussianTime = time => {
  if (!isRussianLocale()) return null;

  return formatWithLocale(time, 'HH:mm');
};

const formatRussianByPattern = (time, datePattern) => {
  if (!isRussianLocale()) return null;

  const hasDateTokens = /[dDLMyY]/.test(datePattern);
  const hasTimeTokens = /[Hhmsa]/.test(datePattern);

  if (hasDateTokens && hasTimeTokens) {
    return formatRussianDateTime(time, { includeTime: true });
  }

  if (hasDateTokens) {
    return formatRussianDateTime(time);
  }

  if (hasTimeTokens) {
    return formatRussianTime(time);
  }

  return null;
};

/**
 * Formats a date-like value into a human-readable time format.
 * @param {number|string|Date} time - Unix timestamp, ISO date, or Date instance.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const russianFormat = formatRussianByPattern(time, dateFormat);
  if (russianFormat) return russianFormat;

  return formatWithLocale(time, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number|string|Date} time - Unix timestamp, ISO date, or Date instance.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = parseDate(time);
  if (!messageTime) return '';

  const russianFormat = formatRussianByPattern(
    messageTime,
    /[Hhmsa]/.test(dateFormat) ? dateFormat : `${dateFormat} HH:mm`
  );
  if (russianFormat) return russianFormat;

  const now = new Date();
  const messageDate = formatWithLocale(messageTime, dateFormat);
  if (!isSameYear(messageTime, now)) {
    return formatWithLocale(messageTime, 'LLL d y, h:mm a');
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number|string|Date} time - Unix timestamp, ISO date, or Date instance.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const parsedDate = parseDate(time);
  if (!parsedDate) return '';

  return formatDistanceToNow(parsedDate, {
    addSuffix: true,
    locale: getDateFnsLocale(),
  });
};

/**
 * Formats a date-like value into a specified date format.
 * @param {number|string|Date} time - Unix timestamp, ISO date, or Date instance.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const russianFormat = formatRussianByPattern(time, df);
  if (russianFormat) return russianFormat;

  return formatWithLocale(time, df);
};

const shortTimestampFromDate = (time, withAgo = false) => {
  const parsedDate = parseDate(time);
  if (!parsedDate) return '';

  const diffInMilliseconds = Date.now() - parsedDate.getTime();
  const absoluteDiff = Math.abs(diffInMilliseconds);

  if (absoluteDiff < MINUTE_IN_MILLISECONDS) {
    return 'now';
  }

  const suffix = withAgo && diffInMilliseconds >= 0 ? ' ago' : '';
  const getUnitValue = unitInMilliseconds => {
    const rawValue = absoluteDiff / unitInMilliseconds;
    return diffInMilliseconds >= 0
      ? Math.floor(rawValue)
      : Math.max(1, Math.round(rawValue));
  };

  if (absoluteDiff < HOUR_IN_MILLISECONDS) {
    const minutes = getUnitValue(MINUTE_IN_MILLISECONDS);
    return `${minutes === 60 ? '1h' : `${minutes}m`}${suffix}`;
  }

  if (absoluteDiff < DAY_IN_MILLISECONDS) {
    const hours = getUnitValue(HOUR_IN_MILLISECONDS);
    return `${hours === 24 ? '1d' : `${hours}h`}${suffix}`;
  }

  if (absoluteDiff < MONTH_IN_MILLISECONDS) {
    const days = getUnitValue(DAY_IN_MILLISECONDS);
    return `${days === 30 ? '1mo' : `${days}d`}${suffix}`;
  }

  if (absoluteDiff < YEAR_IN_MILLISECONDS) {
    const months = getUnitValue(MONTH_IN_MILLISECONDS);
    return `${months === 12 ? '1y' : `${months}mo`}${suffix}`;
  }

  return `${getUnitValue(YEAR_IN_MILLISECONDS)}y${suffix}`;
};

const shortTimestampFromText = (time, withAgo = false) => {
  if (!time) return '';

  const suffix = withAgo ? ' ago' : '';
  const normalizedTime = time.trim().toLowerCase();
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };

  if (timeMappings[normalizedTime]) {
    return timeMappings[normalizedTime];
  }

  return normalizedTime
    .replace(/about\s+|over\s+|almost\s+/g, '')
    .replace(/\s+minute ago$/, `m${suffix}`)
    .replace(/\s+minutes ago$/, `m${suffix}`)
    .replace(/\s+hour ago$/, `h${suffix}`)
    .replace(/\s+hours ago$/, `h${suffix}`)
    .replace(/\s+day ago$/, `d${suffix}`)
    .replace(/\s+days ago$/, `d${suffix}`)
    .replace(/\s+month ago$/, `mo${suffix}`)
    .replace(/\s+months ago$/, `mo${suffix}`)
    .replace(/\s+year ago$/, `y${suffix}`)
    .replace(/\s+years ago$/, `y${suffix}`);
};

/**
 * Converts a date-like value or detailed time description into a shorter format.
 * @param {number|string|Date} time - Date-like value or legacy English relative string.
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  const parsedDate = parseDate(time);
  if (parsedDate) {
    return shortTimestampFromDate(parsedDate, withAgo);
  }

  if (typeof time === 'string') {
    return shortTimestampFromText(time, withAgo);
  }

  return '';
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
